import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  TrendingUp, 
  TrendingDown,
  Users, 
  Package, 
  DollarSign, 
  Calendar,
  ArrowRight,
  RefreshCw,
  Filter,
  Download,
  ChevronDown,
  CheckCircle,
  Clock,
  Truck,
  CreditCard,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, subValue, icon: Icon, trend, trendValue, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend && (
        <div className="mt-3 flex items-center gap-1 text-sm">
          {trend === 'up' ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : (
            <TrendingDown className="w-4 h-4 text-red-500" />
          )}
          <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
            {trendValue}
          </span>
          <span className="text-gray-500">vs last period</span>
        </div>
      )}
    </div>
  );
};

// Pipeline Funnel Component
const PipelineFunnel = ({ funnel }) => {
  const colors = [
    'bg-blue-500',
    'bg-indigo-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-green-500'
  ];

  const maxCount = Math.max(...funnel.map(f => f.total_reached || f.current_count || 1));

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-[#2b3380]" />
        Pipeline Funnel
      </h3>
      <div className="space-y-3">
        {funnel.map((stage, index) => {
          const width = ((stage.total_reached || stage.current_count) / maxCount) * 100;
          return (
            <div key={stage.stage} className="relative">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                <span className="text-sm text-gray-500">
                  {stage.current_count} ({stage.conversion_from_prev})
                </span>
              </div>
              <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
                <div 
                  className={`h-full ${colors[index]} rounded-lg transition-all duration-500 flex items-center justify-end pr-3`}
                  style={{ width: `${Math.max(width, 10)}%` }}
                >
                  <span className="text-white text-xs font-medium">
                    {stage.total_reached || stage.current_count}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Engineer Performance Table
const EngineerPerformance = ({ engineers }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#2b3380]" />
          Engineer Performance
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engineer</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Assigned</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completed</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pending</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rate</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {engineers.map((eng, index) => (
              <tr key={eng._id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#2b3380] text-white flex items-center justify-center text-sm font-medium">
                      {eng.engineer_name?.charAt(0) || '#'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{eng.engineer_name || eng._id}</p>
                      <p className="text-xs text-gray-500">{eng._id}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-gray-800">{eng.total_assigned}</td>
                <td className="px-4 py-3 text-center">
                  <span className="text-green-600 font-medium">{eng.completed}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className="text-orange-600">{eng.pending}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    eng.completion_rate >= 80 ? 'bg-green-100 text-green-700' :
                    eng.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {eng.completion_rate}%
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-medium text-gray-800">
                  ₹{eng.total_value?.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Today's Activity Card
const TodayActivity = ({ data }) => {
  const activities = [
    { label: 'New Leads', value: data?.new_leads || 0, icon: Package, color: 'blue' },
    { label: 'Scheduled Pickups', value: data?.scheduled_pickups || 0, icon: Calendar, color: 'purple' },
    { label: 'Completed', value: data?.completed || 0, icon: CheckCircle, color: 'green' }
  ];

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Today's Activity</h3>
      <div className="grid grid-cols-3 gap-4">
        {activities.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="text-center">
            <div className={`w-12 h-12 mx-auto rounded-xl bg-${color}-50 flex items-center justify-center mb-2`}>
              <Icon className={`w-6 h-6 text-${color}-600`} />
            </div>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Conversion Rates Card
const ConversionRates = ({ rates }) => {
  const items = [
    { from: 'Quote', to: 'Pickup', rate: rates?.quote_to_pickup || '0%', icon: ArrowRight },
    { from: 'Pickup', to: 'PQ', rate: rates?.pickup_to_pq || '0%', icon: ArrowRight },
    { from: 'PQ', to: 'Payment', rate: rates?.pq_to_payment || '0%', icon: ArrowRight }
  ];

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#2b3380]" />
        Conversion Rates
      </h3>
      <div className="space-y-4">
        {items.map(({ from, to, rate }) => (
          <div key={`${from}-${to}`} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{from}</span>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{to}</span>
            </div>
            <span className={`text-lg font-bold ${
              parseFloat(rate) >= 70 ? 'text-green-600' :
              parseFloat(rate) >= 40 ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              {rate}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Dashboard Component
const BuybackDashboard = ({ user, onLogout }) => {
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch summary
      const summaryRes = await fetch(`${API}/api/buyback/dashboard/summary`);
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data.summary);
      }
      
      // Fetch funnel
      const funnelRes = await fetch(`${API}/api/buyback/dashboard/pipeline-funnel`);
      if (funnelRes.ok) {
        const data = await funnelRes.json();
        setFunnel(data.funnel || []);
      }
      
      // Fetch engineer performance
      const engRes = await fetch(`${API}/api/buyback/dashboard/engineer-performance`);
      if (engRes.ok) {
        const data = await engRes.json();
        setEngineers(data.engineers || []);
      }
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      // Demo data
      setSummary({
        total_leads: 156,
        today: { new_leads: 12, scheduled_pickups: 8, completed: 5 },
        pipeline: { quote_generated: 45, pickup_scheduled: 32, pq_completed: 28, payment_done: 20 },
        conversion_rates: { quote_to_pickup: '71.1%', pickup_to_pq: '87.5%', pq_to_payment: '71.4%' },
        revenue: { total_value: 485000, avg_order_value: 24250, completed_orders: 20 }
      });
      setFunnel([
        { stage: 'Quotes Generated', current_count: 45, total_reached: 125, conversion_from_prev: '100%' },
        { stage: 'Pickup Scheduled', current_count: 32, total_reached: 80, conversion_from_prev: '71.1%' },
        { stage: 'PQ Completed', current_count: 28, total_reached: 48, conversion_from_prev: '87.5%' },
        { stage: 'Payment Done', current_count: 20, total_reached: 20, conversion_from_prev: '71.4%' }
      ]);
      setEngineers([
        { _id: 'ENG-001', engineer_name: 'Rajesh Kumar', total_assigned: 45, completed: 38, pending: 3, completion_rate: 84.4, total_value: 152000 },
        { _id: 'ENG-002', engineer_name: 'Amit Singh', total_assigned: 38, completed: 30, pending: 5, completion_rate: 78.9, total_value: 125000 },
        { _id: 'ENG-003', engineer_name: 'Priya Sharma', total_assigned: 32, completed: 28, pending: 2, completion_rate: 87.5, total_value: 98000 }
      ]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b3380]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Buyback Dashboard</h1>
            <p className="text-gray-500">Real-time insights into your buyback operations</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchDashboardData}
              disabled={refreshing}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Leads"
            value={summary?.total_leads || 0}
            subValue="All time"
            icon={Package}
            color="blue"
          />
          <StatCard
            title="Scheduled Pickups"
            value={summary?.pipeline?.pickup_scheduled || 0}
            subValue="In pipeline"
            icon={Calendar}
            trend="up"
            trendValue="+12%"
            color="purple"
          />
          <StatCard
            title="Completed Orders"
            value={summary?.revenue?.completed_orders || 0}
            subValue={`Avg: ₹${(summary?.revenue?.avg_order_value || 0).toLocaleString()}`}
            icon={CheckCircle}
            trend="up"
            trendValue="+8%"
            color="green"
          />
          <StatCard
            title="Total Revenue"
            value={`₹${(summary?.revenue?.total_value || 0).toLocaleString()}`}
            subValue="From completed orders"
            icon={DollarSign}
            trend="up"
            trendValue="+15%"
            color="orange"
          />
        </div>

        {/* Middle Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Today's Activity */}
          <TodayActivity data={summary?.today} />
          
          {/* Conversion Rates */}
          <ConversionRates rates={summary?.conversion_rates} />
          
          {/* Pipeline Stats */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-[#2b3380]" />
              Pipeline Breakdown
            </h3>
            <div className="space-y-3">
              {[
                { label: 'Quote Generated', value: summary?.pipeline?.quote_generated || 0, color: 'bg-blue-500' },
                { label: 'Pickup Scheduled', value: summary?.pipeline?.pickup_scheduled || 0, color: 'bg-purple-500' },
                { label: 'PQ Completed', value: summary?.pipeline?.pq_completed || 0, color: 'bg-pink-500' },
                { label: 'Payment Done', value: summary?.pipeline?.payment_done || 0, color: 'bg-green-500' }
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                    <span className="text-sm text-gray-600">{item.label}</span>
                  </div>
                  <span className="font-semibold text-gray-800">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pipeline Funnel */}
        <div className="mb-6">
          <PipelineFunnel funnel={funnel} />
        </div>

        {/* Engineer Performance */}
        <EngineerPerformance engineers={engineers} />
      </div>
    </Layout>
  );
};

export default BuybackDashboard;
